import { Provider as ReduxProvider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Global as GlobalStyles } from '@emotion/react';
import { BrowserRouter as Router } from 'react-router';

import { reporter } from 'lib/reporter';
import { tracking } from 'lib/tracking';
import { ErrorBoundary } from 'lib/error-boundary';

import {
  Toaster,
  ThemeProvider as DesignSystemThemeProvider,
} from '@ownhealthil/wound-ui';
import { LanguageFacilitator, initI18n } from 'i18n';

import theme from 'styles/theme';
import globalStyles from 'styles/global';

import AppAuthProvider from 'containers/App/AppAuthProvider';

import ErrorBoundaryPage from 'components/ErrorBoundaryPage';
import Loader from 'components/ErrorBoundaryPage/Loader';

import packageJson from '../package.json';
import createStore from './redux/createStore';

const container = document.getElementById('app');
const root = createRoot(container);

const domain = window.location.href;
const endsWithDotSlashRegex = /\.\/$/;
if (domain.match(endsWithDotSlashRegex)) {
  window.location.replace(domain.replace(endsWithDotSlashRegex, ''));
}

const store = createStore();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  },
});

initI18n();

tracking.initTracking({
  token: process.env.MIXPANEL_TOKEN,
  isEnabled:
    process.env.NODE_ENV === 'production' &&
    Boolean(
      process.env.HEALTHY_ENV === 'prod' ||
        process.env.HEALTHY_ENV === 'staging',
    ),
  config: {
    debug: process.env.NODE_ENV === 'development',
  },
});

reporter.init({
  dsn: process.env.SENTRY_DSN,
  version: `spot-portal-${packageJson.version}`,
  environment: process.env.HEALTHY_ENV,
  ignoreErrors: [
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:simulateEvent, ParamCount:\d+$/,
  ],
});

root.render(
  <MuiThemeProvider theme={theme}>
    <DesignSystemThemeProvider theme="wound">
      <ErrorBoundary
        fallbackComponent={ErrorBoundaryPage}
        loaderComponent={Loader}
      >
        <ReduxProvider store={store}>
          <Router>
            <QueryClientProvider client={queryClient}>
              <LanguageFacilitator />
              <ReactQueryDevtools />
              <Toaster />
              <GlobalStyles styles={globalStyles} />
              <AppAuthProvider />
            </QueryClientProvider>
          </Router>
        </ReduxProvider>
      </ErrorBoundary>
    </DesignSystemThemeProvider>
  </MuiThemeProvider>,
);
